<div class="wrapper flex flex-col justify-between">
  <div class="heading text-type-2 mb-[40px]">
    {{ data?.sourceVersionId || dxfVersion ? 'Review map layer' : (isEdit ? 'Edit layer' : 'Add layer') }}
  </div>

  <div class="details flex-1">
    @if (newVersionSourceId) {
      <p class="field-hint mb-[20px]">The current version stays on the map until you review and save this update.</p>
      @if (newVersionExtent) { <p class="field-hint mb-[20px]">This update uses the same saved capture area.</p> }
    }
    <div class="field mb-[20px]">
      <label class="field-label" for="map-layer-name">Name</label>
      <input id="map-layer-name" class="field-input" type="text" [(ngModel)]="name" (ngModelChange)="onNameChange()" maxlength="256"
             [disabled]="submitting || importNameLocked || (!nameEditsMapLayer && (!!data?.sourceVersionId || (isDesignImport && !!dxfVersion)))"
             placeholder="e.g. 16th St — CIP Design" data-testid="map-layer-name" />
      @if (canReviewPublication && publicationLoaded && reviewLayers.length > 1) { <p class="field-hint mt-[4px]">This file adds several map layers. Name each one under Map appearance and group.</p> }
    </div>

    @if (!canReviewPublication) {
    <div class="field mb-[20px]">
      <label class="field-label" for="layer-source">Source</label>
      <mat-form-field class="field-select">
        <mat-select id="layer-source" [(ngModel)]="sourceType" (ngModelChange)="onSourceTypeChange()" data-testid="layer-source-select"
                    [disabled]="isEdit || submitting || !!dxfVersion || !!data?.sourceVersionId || !!newVersionSourceId" aria-label="Layer source" disableOptionCentering>
          <mat-option value="arcgis_url">ArcGIS service URL</mat-option>
          <mat-option value="geojson_upload">Upload GeoJSON file</mat-option>
          <mat-option value="kml_upload">Upload KML/KMZ file</mat-option>
          @if (!isEdit) {
            <mat-option value="dxf_upload">Upload DXF drawing</mat-option>
            <mat-option value="pdf_upload">Upload georeferenced PDF</mat-option>
          }
        </mat-select>
        <mat-icon class="select-caret" matSuffix svgIcon="chevron-light" aria-hidden="true"></mat-icon>
      </mat-form-field>
    </div>

    } @else { <p class="field-hint mb-[12px]" data-testid="layer-provenance">{{ provenanceLabel }}</p> }

    @if (sourceType === 'arcgis_url') {
      <div class="field mb-[20px]">
        <label class="field-label" for="map-layer-url">{{ arcgisMode === 'snapshot' ? 'ArcGIS service or layer URL' : 'FeatureServer layer URL' }}</label>
        <input id="map-layer-url" class="field-input" type="text" [(ngModel)]="sourceUrl" (ngModelChange)="onLiveSourceUrlChange()"
               placeholder="https://services.arcgis.com/…/FeatureServer/0" [disabled]="submitting || !!newVersionSourceId" />
        <div class="field-hint">{{ arcgisMode === 'snapshot' ? 'Use a public HTTPS FeatureServer or MapServer URL. You can select its feature layers below.' : 'Paste the full URL to a single layer (ends with /0, /1, …).' }}</div>
      </div>
      @if (!isEdit && !newVersionSourceId) {
        <div class="field mb-[20px]">
          <p class="field-label">How to use this source</p>
          <mat-radio-group [(ngModel)]="arcgisMode" (ngModelChange)="onLiveSourceUrlChange()" [disabled]="submitting" class="radio-group" aria-label="ArcGIS connection mode" color="primary">
            <mat-radio-button value="snapshot" color="primary">Capture a copy for review</mat-radio-button>
            <mat-radio-button value="live" color="primary">Connect live · online only</mat-radio-button>
          </mat-radio-group>
          <p class="field-hint">{{ arcgisMode === 'snapshot' ? 'Review a fixed copy before adding it to the map. Later service edits do not change this copy.' : 'Service changes appear directly on the map. This connection is not included in offline downloads.' }}</p>
        </div>
      }
      @if (!isEdit && arcgisMode === 'snapshot') {
        <section class="stroked-card publication-summary mb-[20px]" aria-labelledby="arcgis-layers-heading">
          <h3 id="arcgis-layers-heading" class="mat-subtitle-1">Choose service layers</h3>
          <button type="button" mat-button color="primary" (click)="inspectArcGIS()" [disabled]="arcgisInspectBusy || submitting || !sourceUrl.trim()">{{ arcgisInspection ? 'Refresh service layers' : 'Find service layers' }}</button>
          @if (arcgisInspectBusy) { <p class="field-hint" role="status">Checking the ArcGIS service…</p> }
          @for (layer of arcgisLayers; track layer.id) {
            <mat-checkbox [(ngModel)]="layer.selected" [disabled]="!layer.supported || submitting" color="primary">{{ layer.name }}{{ layer.supported ? '' : ' · Cannot capture' }}</mat-checkbox>
          }
          @if (arcgisInspection) {
            <p class="field-hint">Captured layers appear in Pending. Check their location and appearance before choosing Add to map.</p>
            <p class="field-hint">Offline use requires supported service permissions and a verified download.</p>
            @for (warning of arcgisInspection.rendererWarnings; track $index) { <p class="field-hint">{{ warning }}</p> }
          }
          @if (arcgisError) { <p class="field-error" role="alert">{{ arcgisError }}</p> }
        </section>
      }
    } @else if (!canReviewPublication) {
      <div class="field mb-[20px]">
        <label class="field-label">
          {{ sourceFileLabel }}
        </label>

        @if (sourceFilename) {
          <div class="file-name flex flex-row justify-start items-center gap-[5px] mb-[12px] italic">
            <span class="underline underline-offset-4">{{ sourceFilename }}</span>
            @if (canReplaceFile) {
              <button type="button" mat-icon-button (click)="removeSelectedFile()" [disabled]="submitting" aria-label="Remove selected file">
                <mat-icon class="close-icon">close</mat-icon>
              </button>
            }
          </div>
          @if (isEdit && !canReplaceFile) {
            <div class="field-hint">The source file can't be replaced. Delete this layer and re-create it to use a different file.</div>
          }
        }
        @if (canResumeUpload) {
          <p class="field-hint mb-[12px]">Select the original {{ sourceFileLabel }} to finish this upload. Its contents must match the file you started importing.</p>
          @if (dxfFile) {
            <div class="file-name flex flex-row justify-start items-center gap-[5px] mb-[12px]">
              <span>Selected: {{ dxfFile.name }}</span>
              <button type="button" mat-icon-button (click)="removeResumeFile()" [disabled]="submitting" aria-label="Remove selected original file">
                <mat-icon class="close-icon">close</mat-icon>
              </button>
            </div>
          }
        }
        @if ((!sourceFilename && canReplaceFile) || (canResumeUpload && !dxfFile)) {
          <label class="file-dropzone"
                 [class.has-error]="fileError"
                 [class.is-drag-over]="isDragOver"
                 (dragover)="onDragOver($event)"
                 (dragleave)="onDragLeave($event)"
                 (drop)="onFileDrop($event)">
            <input
              type="file"
              class="file-input-hidden"
              [disabled]="submitting"
              [attr.aria-label]="canResumeUpload ? 'Choose the original ' + sourceFileLabel + ' to resume upload' : 'Choose a layer source file'"
              [accept]="sourceFileAccept"
              (change)="onFileSelected($event)" />
            <svg class="file-icon" viewBox="0 0 20 18" fill="none" aria-hidden="true" focusable="false">
              <path
                d="M9.44492 1L17.6525 9.18054C19.4492 10.9714 19.4492 13.8657 17.6523 15.6567C15.8555 17.4478 12.9516 17.4478 11.1549 15.6567L1.9214 6.45363C0.692867 5.22914 0.692867 3.24706 1.9214 2.02257C3.14993 0.798077 5.13856 0.798077 6.36709 2.02257L15.6006 11.2257C16.2607 11.8836 16.2607 12.9536 15.6006 13.6116C14.9404 14.2696 13.8667 14.2696 13.2066 13.6116L4.99923 5.43106"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round" />
            </svg>
            <span class="file-placeholder">
              {{ canResumeUpload ? 'Click or drop the original ' + sourceFileLabel : (isDesignImport ? 'Click or drop a ' + sourceFileLabel : (sourceType === 'kml_upload' ? 'Click or drop a KML or KMZ file' : 'Click or drop a GeoJSON file')) }}
            </span>
            <span class="file-hint">
              {{ isDesignImport ? (importFormat === 'dxf' ? 'ASCII DXF · Max ' + designImportMaximumFileSizeMb + ' MB' : importFormat === 'pdf' ? 'Georeferenced PDF (GeoPDF), page 1 · Max ' + designImportMaximumFileSizeMb + ' MB' : importFormatLabel + ' · Max ' + designImportMaximumFileSizeMb + ' MB') : (sourceType === 'kml_upload' ? 'Max 25 MB' : 'GeoJSON FeatureCollection') }}
            </span>
          </label>
        }

        @if (fileError) {
          <div class="field-error">{{ fileError }}</div>
        }
      </div>
    }

    @if (sourceType === 'arcgis_url' && arcgisMode === 'live') {
      <section class="stroked-card publication-summary mb-[20px]" aria-labelledby="live-preview-heading">
        <h3 id="live-preview-heading" class="mat-subtitle-1">Map preview</h3>
        <p class="field-hint">Live features and styling from the ArcGIS service. Use the map to check the layer's location.</p>
        @if (sourcePreviewBusy) { <p class="field-hint" role="status">Loading map preview…</p> }
        @if (sourcePreviewOpen) {
          <div #sourcePreviewShell class="preview-shell" [class.preview-expanded]="sourcePreviewExpanded">
            <div class="preview-toolbar">
              <label class="field-hint">Opacity <input type="range" class="sn-range" min="0" max="100" step="5" [ngModel]="sourcePreviewOpacity * 100" (ngModelChange)="setPreviewPercent(true, $event)" [style.--fill]="(sourcePreviewOpacity * 100) + '%'" aria-label="Preview opacity" /><span>{{ sourcePreviewOpacity * 100 | number:'1.0-0' }}%</span></label>
            </div>
            <div class="preview-map">
              <div #sourcePreview class="publication-preview" aria-label="ArcGIS layer map preview"></div>
              <ng-container *ngTemplateOutlet="previewControls; context: { source: true, expanded: sourcePreviewExpanded }"></ng-container>
              <ng-container *ngTemplateOutlet="previewFeatureCard"></ng-container>
            </div>
          </div>
        }
        @if (sourcePreviewError) { <p class="field-error" role="alert">{{ sourcePreviewError }}</p> }
        <button type="button" mat-button color="primary" (click)="showLivePreview()" [disabled]="sourcePreviewBusy || !sourceUrl.trim()">{{ sourcePreviewOpen ? 'Refresh map preview' : 'Show map preview' }}</button>
      </section>
    }

    @if (isDesignImport) {
      @if (loadingVersion) {
        <p class="field-hint" role="status">Loading {{ importNoun }}…</p>
      }
      @if (submitting || dxfVersion) {
        <div class="status-card import-status mb-[20px]"
             [class.in-progress]="submitting || importIsProcessing"
             [class.error]="dxfVersion?.status === 'failed'">
          <p class="mat-subtitle-1" role="status" aria-live="polite">
            {{ importPhase || sourceService.statusLabel(dxfVersion?.status) }}
          </p>
          @if (importIsProcessing) {
            <p class="field-hint">You can close this panel and return to Design layers → Pending while processing continues.</p>
          }
          @if (dxfVersion?.status === 'failed') {
            <p class="field-hint">{{ sourceService.failureMessage(dxfVersion?.job?.failureCode, importFormat) }}</p>
          }
          @if (dxfVersion?.status === 'cancelled') {
            <p class="field-hint">This import was cancelled. Start a new import to use another file.</p>
          }
          @if (canReviewPublication) {
            <p class="field-hint">{{ dxfVersion.sublayers.length }} {{ dxfVersion.sublayers.length === 1 ? 'layer' : 'layers' }} · @if (importImageCount) { {{ importImageCount }} {{ importImageCount === 1 ? 'image' : 'images' }}@if (importFeatureCount) { · {{ importFeatureCount }} features } } @else { {{ importFeatureCount }} features }</p>
            <p class="field-hint">{{ dxfVersion.status === 'published' ? 'These layers are already on the map. Preview any changes before updating the map.' : 'Choose the layers to include, check their location and appearance, then add them to the project map.' }}</p>
          }
        </div>
      }
      @if (showLocationInputs) {
        <div class="location-fields">
          <p class="field-hint">Confirm the coordinate system and units with the file's author. These values determine where it appears on the map.</p>
          <div class="field">
            <label class="field-label" for="drawing-crs">Coordinate system (EPSG)</label>
            <div class="field-prefixed" [class.is-disabled]="submitting">
              <span class="field-prefix" aria-hidden="true">EPSG:</span>
              <input id="drawing-crs" class="field-input" type="text" inputmode="numeric" pattern="[0-9]*"
                     [(ngModel)]="sourceCrsCode" [disabled]="submitting" maxlength="6" autocomplete="off"
                     placeholder="2278" aria-describedby="drawing-crs-hint" />
            </div>
            <p id="drawing-crs-hint" class="field-hint">The EPSG code supplied with this file, for example 2278 for Texas South Central in US survey feet.</p>
          </div>
          <div class="field">
            <label class="field-label" for="drawing-units">Source units</label>
            <mat-form-field class="field-select">
              <mat-select id="drawing-units" [(ngModel)]="sourceUnits" [disabled]="submitting" placeholder="Select units" aria-label="Source units" disableOptionCentering>
                <mat-option value="metre">Metres</mat-option>
                <mat-option value="foot">International feet</mat-option>
                <mat-option value="us_survey_foot">US survey feet</mat-option>
                <mat-option value="degree">Degrees</mat-option>
              </mat-select>
        <mat-icon class="select-caret" matSuffix svgIcon="chevron-light" aria-hidden="true"></mat-icon>
            </mat-form-field>
          </div>
        </div>
      }
      @if (reviewWarnings.length) {
        <div class="stroked-card import-warnings mb-[20px]">
          <h3 class="mat-subtitle-1">Review notes</h3>
          <ul class="mat-caption">
            @for (warning of reviewWarnings; track $index) {
              <li>{{ warning }}</li>
            }
          </ul>
        </div>
      }
      @if (canReviewPublication && publishingService.canConfigure(projectId)) {
        <section class="publication-review" aria-labelledby="publication-review-heading">
          <div class="stroked-card publication-summary">
            <h3 class="mat-subtitle-1">Check the location</h3>
            <p class="field-hint">Compare the imported location with known features on the basemap. This preview draws the source with the appearance settings below, so a color or symbol change shows here right away.</p>
            @if (sourcePreviewBusy) { <p class="field-hint" role="status">Loading location preview…</p> }
            @if (sourcePreviewOpen) {
              <div #sourcePreviewShell class="preview-shell" [class.preview-expanded]="sourcePreviewExpanded">
                <div class="preview-toolbar">
                  <label class="field-hint">Opacity <input type="range" class="sn-range" min="0" max="100" step="5" [ngModel]="sourcePreviewOpacity * 100" (ngModelChange)="setPreviewPercent(true, $event)" [style.--fill]="(sourcePreviewOpacity * 100) + '%'" aria-label="Preview opacity" /><span>{{ sourcePreviewOpacity * 100 | number:'1.0-0' }}%</span></label>
                </div>
                <div class="preview-map">
                  <div #sourcePreview class="publication-preview" aria-label="Imported source location map preview"></div>
                  <ng-container *ngTemplateOutlet="previewControls; context: { source: true, expanded: sourcePreviewExpanded }"></ng-container>
              <ng-container *ngTemplateOutlet="previewFeatureCard"></ng-container>
                </div>
              </div>
            }
            @if (renderNotes.length) {
              <ul class="field-hint render-notes">
                @for (note of renderNotes; track $index) {
                  <li>{{ note }}</li>
                }
              </ul>
            }
            @if (sourcePreviewFootprint) {
              <p class="field-hint">{{ sourcePreviewHint }}</p>
            }
            @if (sourcePreviewError) { <p class="field-error" role="alert">{{ sourcePreviewError }}</p> }
          </div>
          <h3 id="publication-review-heading" class="mat-subtitle-1">Map appearance and group</h3>
          @if (!publicationLoaded && !publicationBusy) {
            <button type="button" mat-button color="primary" (click)="loadPublicationReview()">Load map settings</button>
          }
          @if (publicationLoaded) {
            <fieldset class="publication-fields">
              <legend class="field-label">Choose source layers</legend>
              <div class="review-table-wrapper">
                @for (layer of reviewLayers; track layer.key) {
                  <div class="review-layer-row">
                    <mat-checkbox class="review-select" [(ngModel)]="layer.selected" color="primary"
                                  (ngModelChange)="publicationConfigurationChanged()" [attr.aria-label]="'Include ' + layer.name" />
                    @if (reviewLayers.length > 1) {
                      <div class="field">
                        <label class="field-hint" [for]="'review-name-' + $index">{{ layer.key }}</label>
                        <input class="field-input" [id]="'review-name-' + $index" [(ngModel)]="layer.name" maxlength="256" aria-label="Map layer name" />
                      </div>
                    } @else { <span class="field-hint">{{ layer.key }}</span> }
                  </div>
                }
              </div>
              <div class="publication-grid">
                @if (hasSelectedVectors) {
                <div class="field"><label class="field-label" for="review-color">Layer color</label>
                  <input id="review-color" class="field-input" type="color" [(ngModel)]="reviewSettings.color" (ngModelChange)="publicationConfigurationChanged()" /></div>
                @if (colorByOptions.length) {
                <div class="field"><label class="field-label" for="review-color-by">Color by</label>
                  <mat-form-field class="field-select">
                    <mat-select id="review-color-by" [ngModel]="colorByField" (ngModelChange)="setColorByField($event)" aria-label="Color by" data-testid="review-color-by" disableOptionCentering>
                      <mat-option value="">No field</mat-option>
                      @for (field of colorByOptions; track field) {
                        <mat-option [value]="field">{{ colorByOptionLabel(field) }}</mat-option>
                      }
                    </mat-select>
                    <mat-icon class="select-caret" matSuffix svgIcon="chevron-light" aria-hidden="true"></mat-icon>
                  </mat-form-field></div>
                }
                <div class="field"><label class="field-label" for="review-width">Line width (px)</label>
                  <input id="review-width" class="field-input" type="number" min="0" max="64" step="0.5" [(ngModel)]="reviewSettings.strokeWidth" (ngModelChange)="publicationConfigurationChanged()" /></div>
                <div class="field"><label class="field-label" for="review-point-size">Point size (px)</label>
                  <input id="review-point-size" class="field-input" type="number" min="0" max="128" [(ngModel)]="reviewSettings.pointSize" (ngModelChange)="publicationConfigurationChanged()" /></div>
                <div class="field"><label class="field-label" for="review-fill">Area opacity (%)</label>
                  <input id="review-fill" class="field-input" type="number" min="0" max="100" step="5" [ngModel]="reviewSettings.fillAlpha * 100" (ngModelChange)="setReviewOpacity('fillAlpha', $event)" /></div>
                }
              </div>
              @if (hasSelectedVectors && sourceColorHint) {
                <p class="field-hint" data-testid="source-color-hint">{{ sourceColorHint }}</p>
              }
              @if (hasSelectedVectors && reviewSettings.colorBy) {
                <div class="field" data-testid="color-by-legend">
                  <span class="field-label">{{ colorByLegendHeading }}</span>
                  <div class="color-by-values">
                    @for (row of visibleColorByRows; track row.label) {
                      <div class="color-by-row" data-testid="color-by-row">
                        <span class="color-by-swatches">
                          @for (entry of row.entries; track $index) {
                            <input class="color-by-swatch" type="color" [(ngModel)]="entry.color"
                                   (ngModelChange)="publicationConfigurationChanged()" [attr.aria-label]="'Color for ' + (entry.label || entry.value) + (row.entries.length > 1 ? ' ' + ($index + 1) : '')" />
                          }
                        </span>
                        <span class="color-by-value" [title]="row.label">{{ row.label }}@if (row.entries.length > 1) {<span class="color-by-count"> · {{ row.entries.length }} colors</span>}</span>
                      </div>
                    }
                  </div>
                  @if (colorByRows.length > colorByCollapsedCount) {
                    <button type="button" class="link-button color-by-toggle" data-testid="color-by-toggle" (click)="colorByExpanded = !colorByExpanded">
                      {{ colorByExpanded ? 'See less' : '+' + (colorByRows.length - colorByCollapsedCount) + ' more' }}
                    </button>
                  }
                  <p class="field-hint">Each value publishes in its own color and appears in the map legend. Features without this field use the layer color.</p>
                </div>
              }
              @if (importImageCount) {
                <p class="field-hint">Images retain their original colors and transparency. They are view-only and have no feature popups. Color and symbol settings apply to vector layers only.</p>
              }
              @if (hasSelectedVectors) {
              <div class="field"><label class="field-label" for="review-popup-fields">Popup fields</label>
                <input id="review-popup-fields" class="field-input" [(ngModel)]="reviewSettings.popupFields" maxlength="4096"
                       (ngModelChange)="publicationConfigurationChanged()" (blur)="normalizePopupFields()" placeholder="Field names, separated by commas" />
                @if (unknownPopupFields.length) {
                  <p class="field-error" role="alert">Not in this file: {{ unknownPopupFields.join(', ') }}. These will show as blank on the map.</p>
                }
                @if (sourceFieldNames.length) {
                  <p class="field-hint field-chips" aria-label="Fields in this file">Fields in this file:
                    @for (name of sourceFieldNames; track name) {
                      <button type="button" class="field-chip" (click)="addPopupField(name)" [disabled]="popupFieldChosen(name)">{{ name }}</button>
                    }
                  </p>
                }
                <p class="field-hint">The feature card on the map shows only these fields. This style does not add text labels.</p>
              </div>
              }
              <div class="field"><label class="field-label" for="review-group">Group</label>
                <mat-form-field class="field-select">
                  <mat-select id="review-group" [(ngModel)]="groupId" (ngModelChange)="reviewed = false" aria-label="Group" data-testid="review-group" disableOptionCentering>
                    <mat-option [value]="null">No group</mat-option>
                    @for (group of publicationCatalog?.groups || []; track group.id) { <mat-option [value]="group.id">{{ group.name }}</mat-option> }
                    <mat-option [value]="''">Create a group</mat-option>
                  </mat-select>
                  <mat-icon class="select-caret" matSuffix svgIcon="chevron-light" aria-hidden="true"></mat-icon>
                </mat-form-field>
              </div>
              @if (groupId === '') {
                <div class="field"><label class="field-label" for="review-new-group">Group name</label>
                  <input id="review-new-group" class="field-input" [(ngModel)]="newGroupName" maxlength="256" (ngModelChange)="reviewed = false" /></div>
              }
            </fieldset>
            <fieldset class="publication-fields">
              <div class="publication-grid">
                <div class="field"><label class="field-label" for="review-order">Starting layer order</label>
                  <input id="review-order" class="field-input" type="number" min="0" max="2147483600" [(ngModel)]="layerOrder" (ngModelChange)="reviewed = false" /></div>
                <div class="field"><label class="field-label" for="review-opacity">Default opacity (%)</label>
                  <input id="review-opacity" class="field-input" type="number" min="0" max="100" step="5" [ngModel]="reviewSettings.defaultOpacity * 100" (ngModelChange)="setReviewOpacity('defaultOpacity', $event)" /></div>
              </div>
              <div class="setting-row" data-testid="review-default-visible">
                <div class="setting-copy">
                  <div class="setting-title">Visible by default on the project map</div>
                  <div class="setting-description">Crews can still turn it on or off for themselves in the layers panel.</div>
                </div>
                <mat-slide-toggle [checked]="reviewSettings.defaultVisible" (change)="reviewSettings.defaultVisible = $event.checked; reviewed = false" color="primary" aria-label="Visible by default on the project map" />
              </div>
            </fieldset>
          }
          @if (preparedRenditions.length) {
            <div class="stroked-card publication-summary">
              @if (dxfVersion.capture?.captureInterval?.finishedAt) { <p class="field-hint">Source captured {{ dxfVersion.capture.captureInterval.finishedAt | date:'medium' }}</p> }
              @if (previewExpired) { <p class="field-hint" role="status">Preview access expired.</p> }
              @if (previewOpen) {
                <div #previewShell class="preview-shell" [class.preview-expanded]="previewExpanded">
                  <div class="preview-toolbar">
                    <label class="field-hint">Opacity <input type="range" class="sn-range" min="0" max="100" step="5" [ngModel]="previewOpacity * 100" (ngModelChange)="setPreviewPercent(false, $event)" [style.--fill]="(previewOpacity * 100) + '%'" aria-label="Preview opacity" /><span>{{ previewOpacity * 100 | number:'1.0-0' }}%</span></label>
                  </div>
                  <div class="preview-map">
                    <div #publicationPreview class="publication-preview" aria-label="Map layer preview"></div>
                    <ng-container *ngTemplateOutlet="previewControls; context: { source: false, expanded: previewExpanded }"></ng-container>
                    <ng-container *ngTemplateOutlet="previewFeatureCard"></ng-container>
                  </div>
                </div>
                @if (!previewReady && !publicationError) { <p class="field-hint" role="status">{{ previewRenderedCount }} selected layers have loaded. Pan or zoom to review the remaining layers before saving to the map.</p> }
              }
              @if (previewReady && !skipsReviewConfirmation) {
                <mat-checkbox class="review-check" data-testid="review-confirm" [(ngModel)]="reviewed" [disabled]="publicationBusy" color="primary">I reviewed the location, warnings, selected layers, and project defaults.</mat-checkbox>
              }
            </div>
          }
          @if (publicationError && publicationStatus !== 'failed') { <p class="field-error" role="alert">{{ publicationError }}</p> }
          @if (publicationConflict) {
            <button type="button" mat-button color="primary" (click)="reloadPublicationCatalog()" [disabled]="publicationBusy">Reload latest map</button>
          }
        </section>
      }

    }
    @if (submitError) {
      <div class="field-error mb-[20px]" role="alert">{{ submitError }}</div>
    }
    @if (pollingError) {
      <button type="button" mat-button color="primary" (click)="refreshImportStatus()" [disabled]="submitting">Refresh import status</button>
    }
  </div>

  <!-- One primary action for the whole drawer. The status row above it says
       whether the prepared output still matches the settings, so the reviewer
       never has to hunt for a build button. -->
  <div class="action-section">
    @if (reviewingPublication && publicationLoaded) {
      <p class="footer-status" role="status" aria-live="polite" data-testid="preview-status">
        @switch (publicationStatus) {
          @case ('working') { <span>{{ publicationPhase || 'Working…' }}</span> }
          @case ('saved') { <span>{{ publicationPhase }}</span> }
          @case ('ready') {
            <span>{{ isPublishedLayer ? 'Ready to save' : 'Ready to add' }} · {{ preparedBytes / 1048576 | number:'1.1-1' }} MB · {{ publicationAttributionLabel }}@if (reviewWarnings.length) { · See notes above }</span>
            @if (!previewOpen) {
              <button type="button" class="link-button" data-testid="preview-show" (click)="previewPublication()">Show preview</button>
            }
          }
          @case ('failed') {
            <span class="footer-status-error">{{ publicationError || 'The map preview could not be prepared.' }}</span>
            @if (preparationStillRunning) {
              <button type="button" class="link-button" data-testid="preview-recheck" (click)="refreshPreparation()">Check again</button>
            } @else {
              <button type="button" class="link-button" data-testid="preview-retry" (click)="preparePublication(true)">Try again</button>
            }
          }
          @default {
            <span>{{ staleStatus.label }}</span>
            <button type="button" class="link-button" data-testid="preview-update" (click)="preparePublication(true)" [disabled]="publicationBusy">{{ staleStatus.action }}</button>
          }
        }
      </p>
    }
    <div class="action-row">
      @if (reviewingPublication) {
        @if (publishingService.canPublish(projectId)) {
          <app-primary-button (handleClick)="addToMap()" [isDisabled]="!canAddToMap" data-testid="add-to-map">{{ addToMapLabel }}</app-primary-button>
        } @else {
          <p class="field-hint">Someone with permission to update project map layers must add these layers to the map.</p>
        }
      } @else if (!(isDesignImport && importIsFinished)) {
        <app-primary-button (handleClick)="submit()" [isDisabled]="!canSubmit() || submitting">
          {{ isDesignImport ? importActionLabel : (submitting ? (isEdit ? 'Saving…' : 'Adding…') : (isEdit ? 'Save' : (sourceType === 'arcgis_url' && arcgisMode === 'snapshot' ? 'Capture for review' : 'Add layer'))) }}
        </app-primary-button>
      }
      @if (isDesignImport && importIsProcessing) {
        <app-secondary-button (handleClick)="cancelImport()" [isDisabled]="submitting">Cancel import</app-secondary-button>
      }
    </div>
  </div>
</div>

<!-- One control rail for every preview map on this panel. `source` picks the map
     it drives (the source/location preview or the prepared preview); `expanded`
     swaps Expand for the zoom controls and the close X. -->
<ng-template #previewFeatureCard>
  @if (previewFeatures !== null || previewFeaturesNote || previewFeaturesBusy) {
    <section class="reference-inspection preview-inspection" role="dialog" aria-label="Feature properties" data-testid="prepared-preview-features"
             [attr.aria-busy]="previewFeaturesBusy" (mouseleave)="hoverPreviewFeature(null)">
      <div class="reference-inspection-header">
        @if (previewFeatureView === 'details' && (previewFeatures?.length || 0) > 1) {
          <button type="button" class="preview-inspection-back" aria-label="Back to the list" (click)="backToPreviewFeatures()"><mat-icon svgIcon="chevron-left"></mat-icon></button>
        }
        <h3 class="preview-inspection-title" [matTooltip]="previewFeatureView === 'details' ? selectedPreviewFeature?.label : null" matTooltipClass="project-name-tooltip">
          {{ previewFeaturesBusy ? 'Looking up…' : (previewFeatureView === 'details' ? selectedPreviewFeature?.label : 'Features here') }}
        </h3>
        <button type="button" class="preview-inspection-close" aria-label="Close feature properties" (click)="closePreviewFeatures()">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M15 5L5 15M5 5L15 15" stroke="#060B1E" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
      </div>
      @if (previewFeatureView === 'list' && (previewFeatures?.length || 0) > 1) {
        <ul class="preview-inspection-list" aria-label="Features under the click">
          @for (card of previewFeatures; track card.key) {
            <li>
              <button type="button" class="preview-inspection-row" (click)="selectPreviewFeature(card)" (mouseenter)="hoverPreviewFeature(card)" (focus)="hoverPreviewFeature(card)">
                <span class="preview-inspection-row-text">
                  <span class="text-type-14 preview-inspection-row-label" [matTooltip]="card.label" matTooltipClass="project-name-tooltip" matTooltipShowDelay="400">{{ card.label }}</span>
                  @if (card.meta) { <span class="reference-inspection-secondary preview-inspection-row-sub">{{ card.meta }}</span> }
                </span>
                <mat-icon class="preview-inspection-row-caret" svgIcon="chevron-left" aria-hidden="true"></mat-icon>
              </button>
            </li>
          }
        </ul>
        @if (previewFeaturesHidden) {
          <p class="text-type-14 reference-inspection-secondary preview-inspection-more" role="status">{{ previewFeaturesHidden }} more nearby. Zoom in to narrow it down.</p>
        }
      }
      @if (previewFeatureView === 'details' && selectedPreviewFeature; as selected) {
        <div class="reference-inspection-content">
          @if (previewShowsSublayers && selected.sublayer) { <h4 class="text-type-11 reference-inspection-secondary">{{ selected.sublayer }}</h4> }
          <dl class="reference-inspection-fields text-type-14">
            @for (field of selected.fields; track field.label) {
              <div><dt>{{ field.label }}</dt><dd>{{ field.value }}</dd></div>
            }
          </dl>
          @if (!selected.fields.length && !previewFeaturesNote) { <p class="text-type-14 reference-inspection-secondary">None of the chosen popup fields are set on this feature.</p> }
        </div>
      }
      @if (previewFeaturesNote) { <p class="text-type-14 reference-inspection-secondary preview-inspection-more" role="status">{{ previewFeaturesNote }}</p> }
    </section>
  }
</ng-template>

<ng-template #previewControls let-source="source" let-expanded="expanded">
  <div class="preview-controls" [class.preview-controls-expanded]="expanded">
    @if (expanded) {
      <button type="button" class="control-button preview-close-button" (click)="togglePreviewExpanded(source)"
              aria-label="Close map" title="Close map"
              [attr.data-testid]="(source ? 'source' : 'prepared') + '-preview-close'">
        <mat-icon>close</mat-icon>
      </button>
    }
    <div class="preview-rail">
      <button type="button" class="control-button" (click)="refreshPreview(source)"
              [disabled]="source ? sourcePreviewBusy : publicationBusy"
              [attr.aria-label]="source ? 'Refresh location preview' : 'Refresh map preview'"
              [title]="source ? 'Refresh location preview' : 'Refresh map preview'"
              [attr.data-testid]="(source ? 'source' : 'prepared') + '-preview-refresh'">
        <mat-icon>refresh</mat-icon>
      </button>
      <button type="button" class="control-button" (click)="zoomPreview(source)" [disabled]="source && sourcePreviewBusy"
              aria-label="Zoom to layer" title="Zoom to layer"
              [attr.data-testid]="(source ? 'source' : 'prepared') + '-preview-fit'">
        <mat-icon>center_focus_strong</mat-icon>
      </button>
      <button type="button" class="control-button" (click)="togglePreviewBasemap(source)"
              [attr.aria-pressed]="previewSatelliteOn(source)"
              [attr.aria-label]="previewSatelliteOn(source) ? 'Show default basemap' : 'Show satellite imagery'"
              [title]="previewSatelliteOn(source) ? 'Show default basemap' : 'Show satellite imagery'"
              [attr.data-testid]="(source ? 'source' : 'prepared') + '-preview-basemap'">
        <mat-icon>{{ previewSatelliteOn(source) ? 'map' : 'satellite_alt' }}</mat-icon>
      </button>
      @if (expanded) {
        <button type="button" class="control-button" (click)="zoomPreviewBy(source, 1)" aria-label="Zoom in" title="Zoom in"
                [attr.data-testid]="(source ? 'source' : 'prepared') + '-preview-zoom-in'">
          <mat-icon>add</mat-icon>
        </button>
        <button type="button" class="control-button" (click)="zoomPreviewBy(source, -1)" aria-label="Zoom out" title="Zoom out"
                [attr.data-testid]="(source ? 'source' : 'prepared') + '-preview-zoom-out'">
          <mat-icon>remove</mat-icon>
        </button>
      } @else {
        <button type="button" class="control-button" (click)="togglePreviewExpanded(source)" aria-label="Expand map" title="Expand map"
                [attr.data-testid]="(source ? 'source' : 'prepared') + '-preview-expand'">
          <mat-icon>open_in_full</mat-icon>
        </button>
      }
    </div>
  </div>
</ng-template>
